import { Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadBlob, downloadText, exportAccountingTouchDetail } from "@/lib/api";
import { ReportMultiSelect, appendRepeatQuery } from "@/pages/dashboard/admin/ReportMultiSelect";
import { useAccountingTouchDetailViewModel } from "@/pages/dashboard/admin/useAccountingTouchDetailViewModel";

export function AccountingTouchDetailView() {
  const {
    isAdmin,
    accountingFromDate, setAccountingFromDate,
    accountingToDate, setAccountingToDate,
    accountingCoCode, setAccountingCoCode,
    accountingOfficeLocation, setAccountingOfficeLocation,
    accountingFormat, setAccountingFormat,
    accountingExportError, setAccountingExportError,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    currentCalendarMonthRange,
    accountingTouchDetailFilename,
  } = useAccountingTouchDetailViewModel();
  return (
    <>
                {isAdmin && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex flex-wrap items-center gap-2">
                        Accounting Touch Detail (Month)
                      </CardTitle>
                      <CardDescription>
                        Punch-level hours for coding: customer id + class id, Regular/OT like payroll. Default LXB. India and Hong Kong offices omitted.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-end gap-3">
                      <ReportMultiSelect
                        label="Co Code"
                        allLabel="All co codes"
                        options={payrollCoCodesData?.co_codes ?? []}
                        optionLabels={payrollCoCodesData?.companies}
                        className="min-w-[220px]"
                        value={accountingCoCode}
                        onChange={setAccountingCoCode}
                        loading={payrollCoCodesLoading}
                        error={!!payrollCoCodesError}
                      />

                      <ReportMultiSelect
                        label="Office Location"
                        allLabel="All offices (except India & Hong Kong)"
                        options={(officeLocationsData?.office_locations ?? []).filter((o) => {
                          const low = o.toLowerCase();
                          return !low.includes("india") && !low.includes("hong kong");
                        })}
                        value={accountingOfficeLocation}
                        onChange={setAccountingOfficeLocation}
                        loading={officeLocationsLoading}
                        error={!!officeLocationsError}
                        className="min-w-[200px]"
                      />

                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">From</Label>
                        <Input type="date" value={accountingFromDate || ""} onChange={(e) => setAccountingFromDate(e.target.value)} className="w-[150px]" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">To</Label>
                        <Input type="date" value={accountingToDate || ""} onChange={(e) => setAccountingToDate(e.target.value)} className="w-[150px]" />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const range = currentCalendarMonthRange();
                          setAccountingFromDate(range.from);
                          setAccountingToDate(range.to);
                        }}
                      >
                        This month
                      </Button>
                      <div className="grid gap-1 min-w-[120px]">
                        <Label className="text-xs text-muted-foreground">Format</Label>
                        <Select value={accountingFormat} onValueChange={(v) => setAccountingFormat(v as "csv" | "xlsx")}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="xlsx">XLSX</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="button" onClick={async () => {
                        setAccountingExportError(null);
                        try {
                          const params = new URLSearchParams();
                          if (accountingFromDate) params.set("from_date", accountingFromDate);
                          if (accountingToDate) params.set("to_date", accountingToDate);
                          appendRepeatQuery(params, "co_code", accountingCoCode);
                          appendRepeatQuery(params, "office_location", accountingOfficeLocation);
                          const result = await exportAccountingTouchDetail(params, accountingFormat);
                          const filename = accountingTouchDetailFilename(accountingFromDate, accountingToDate, accountingFormat);
                          if (accountingFormat === "xlsx") {
                            downloadBlob(filename, result as Blob);
                          } else {
                            downloadText(filename, result as string, "text/csv;charset=utf-8");
                          }
                        } catch (e: unknown) {
                          setAccountingExportError(e instanceof Error ? e.message : "Export failed");
                        }
                      }} disabled={!isAdmin}>
                        <Download className="size-4" /> Export Accounting
                      </Button>
                      {accountingExportError && <div className="text-xs text-destructive w-full">{accountingExportError}</div>}
                    </CardContent>
                  </Card>
                )}
    </>
  );
}
